import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useApolloClient, useReactiveVar, gql } from "@apollo/client";
import ErrorScreen from "./ErrorScreen";
import LoadingScreen from "./LoadingScreen";
import Button, { ButtonVariant } from "./Button";
import ExperienceBlock from "./ExperienceBlock";
import TitleBar, { WithfriendsAction } from "./WithfriendsTitleBar";
import NotConnectedScreen from "./NotConnectedScreen";
import { useUpdateBrandV1Mutation, useUpdateBrandV2Mutation, useUpdateThemeMutation, useExperienceScreenQueryV1Query, useExperienceScreenQueryV2Query, UpdateBrandFragmentV1FragmentDoc, UpdateBrandFragmentV2FragmentDoc, UpdateThemeFragmentDoc } from "../types";
import { currentBusinessIdVar, currentShopifyVersionVar, startPrimaryAction, completePrimaryAction, primaryActionLoadingVar, dummyFunc } from "../helpers/client";
import { useAppBridge } from "@shopify/app-bridge-react";
import { Redirect } from "@shopify/app-bridge/actions";
// @ts-ignore
import { useNavigate } from "react-router-dom";
import ExperienceScreenPreferences from "./ExperienceScreenPreferences";
import { Banner } from "@shopify/polaris";
gql `
fragment ExperienceScreenQuery on Business {
  id
  launchPressedDate
  theme {
    id
  }
  ...ExperienceScreenPreferences
}

query ExperienceScreenQueryV1($id: ID!) {
  business(id: $id) {
    ...ExperienceScreenQuery
  }
}

query ExperienceScreenQueryV2($id: ID!) {
  business(id: $id) {
    ...ExperienceScreenQuery
    membershipTiers {
      shopifyHandle
    }
    shopifyMemberExperienceAppEmbedBlockPublished
    shopifyBadgeAppEmbedBlockPublished
    shopifyCartUpsellAppEmbedBlockPublished
    shopifyAppBlockPublished
  }
}
`;
const ExperienceScreen = ({ useSyncShopifyDataMutation = dummyFunc, standalone = false }) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const currentBusinessId = useReactiveVar(currentBusinessIdVar);
    const currentShopifyVersion = useReactiveVar(currentShopifyVersionVar);
    const { data, loading, error } = currentShopifyVersion != 2 ? useExperienceScreenQueryV1Query({ variables: { id: currentBusinessId } }) : useExperienceScreenQueryV2Query({ variables: { id: currentBusinessId }, pollInterval: 7500 });
    const primaryActionLoading = useReactiveVar(primaryActionLoadingVar);
    // TODO - can't seem to abstract this out without React crashing, so we gotta copy and paste it into any relevant file
    let app, redirect = null;
    let navigate = useNavigate();
    let isEmbedded = false;
    try {
        app = useAppBridge();
        redirect = Redirect.create(app);
        isEmbedded = true;
    }
    catch (e) { }
    const VisitLink = (url) => {
        (redirect ?
            redirect.dispatch(Redirect.Action.APP, url) :
            navigate(url));
    };
    // TODO - end
    const client = useApolloClient();
    const [updateBrandV1] = useUpdateBrandV1Mutation();
    const [updateBrandV2] = useUpdateBrandV2Mutation();
    const [updateTheme] = useUpdateThemeMutation();
    const [syncToShopify] = useSyncShopifyDataMutation({
        variables: {
            id: currentBusinessId
        },
        skip: !currentBusinessId,
    });
    if (!currentBusinessId)
        return _jsx(NotConnectedScreen, { action: WithfriendsAction.Brand });
    if (loading)
        return _jsx(LoadingScreen, {});
    if (error)
        return _jsx(ErrorScreen, { message: error.message });
    if (!data)
        return null;
    const saveExperience = async () => {
        var _a, _b, _c, _d;
        startPrimaryAction();
        try {
            const businessCacheData = client.readFragment({
                id: `Business:${currentBusinessId}`,
                fragment: ((_a = data.business) === null || _a === void 0 ? void 0 : _a.shopifyVersion) != 2 ? UpdateBrandFragmentV1FragmentDoc : UpdateBrandFragmentV2FragmentDoc
            });
            const updateParams = {
                variables: {
                    id: currentBusinessId,
                    business: businessCacheData,
                },
            };
            if (((_b = data.business) === null || _b === void 0 ? void 0 : _b.shopifyVersion) != 2)
                await updateBrandV1(updateParams);
            else
                await updateBrandV2(updateParams);
            // Theme
            if ((_d = (_c = data === null || data === void 0 ? void 0 : data.business) === null || _c === void 0 ? void 0 : _c.theme) === null || _d === void 0 ? void 0 : _d.id) {
                const themeCacheData = client.readFragment({
                    id: `Theme:${data.business.theme.id}`,
                    fragment: UpdateThemeFragmentDoc,
                });
                const themeUpdateParams = {
                    variables: {
                        businessId: currentBusinessId,
                        theme: themeCacheData,
                    },
                };
                await updateTheme(themeUpdateParams);
            }
        }
        catch (err) { }
        completePrimaryAction();
    };
    const standaloneButton = async () => {
        await saveExperience();
        await syncToShopify();
    };
    const settingsButton = async () => {
        await saveExperience();
        await syncToShopify();
        VisitLink("/brand");
    };
    const continueButton = async () => {
        await saveExperience();
        await syncToShopify();
        VisitLink("/launch");
    };
    // @ts-ignore
    const needsBlock = ((_a = data === null || data === void 0 ? void 0 : data.business) === null || _a === void 0 ? void 0 : _a.shopifyVersion) == 2 && (!((_b = data === null || data === void 0 ? void 0 : data.business) === null || _b === void 0 ? void 0 : _b.shopifyMemberExperienceAppEmbedBlockPublished) || ((_c = data === null || data === void 0 ? void 0 : data.business) === null || _c === void 0 ? void 0 : _c.shopifyBadgeAppEmbedBlockPublished) || ((_d = data === null || data === void 0 ? void 0 : data.business) === null || _d === void 0 ? void 0 : _d.shopifyCartUpsellAppEmbedBlockPublished) || ((_e = data === null || data === void 0 ? void 0 : data.business) === null || _e === void 0 ? void 0 : _e.shopifyAppBlockPublished));
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: `
          .wf-experience-screen .Polaris-Banner.Polaris-Banner--withinPage {
              width: 100%;
          }
        ` }), _jsxs("div", { className: "w-full flex flex-col justify-center wf-experience-screen", children: [_jsx(TitleBar, { primaryAction: WithfriendsAction.SaveExperience, action: WithfriendsAction.Experience, useSyncShopifyDataMutation: useSyncShopifyDataMutation }), _jsx("div", { className: "w-full flex justify-center", children: _jsx("div", { className: "w-full max-w-2xl flex flex-wrap", children: _jsxs("div", { className: "flex flex-wrap", children: [!standalone && (_jsxs(_Fragment, { children: [_jsx("h2", { className: "text-4xl font-light max-w-xl w-full mb-4", children: "Enable Your Member Experience." }), _jsxs("p", { className: "prose prose-lg max-w-full mb-8", children: ["Preview your member experience features in your brand", ((_f = data.business) === null || _f === void 0 ? void 0 : _f.shopifyVersion) == 2 ? " and theme" : "", ", and use the checkbox to enable or disable them."] }), (((_g = data.business) === null || _g === void 0 ? void 0 : _g.shopifyVersion) == 2 && isEmbedded) ? (_jsx("div", { className: "mb-8 w-full", children: _jsx(Banner, { title: needsBlock ? "Required." : "All set! You've taken care of this.", tone: needsBlock ? "warning" : "success", children: _jsx(ExperienceBlock, { business: data === null || data === void 0 ? void 0 : data.business, fromExperiencePage: true }) }) })) : null] })), _jsx("div", { className: "mb-5 mx-auto max-w-4xl", children: _jsx(ExperienceScreenPreferences, { business: data.business, saveExperience: saveExperience }) })] }) }) }), !standalone ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "w-full flex flex-wrap justify-center mb-4", children: _jsx("div", { className: "flex max-w-xl w-full", children: _jsx(Button, { variant: ButtonVariant.Outlined, onClick: settingsButton, disabled: primaryActionLoading, children: "Save and Return to Editing Brand" }) }) }), _jsx("div", { className: "w-full flex flex-wrap justify-center", children: _jsx("div", { className: "flex max-w-xl w-full mb-16 sm:mb-0", children: _jsx(Button, { variant: ButtonVariant.Salmon, onClick: continueButton, disabled: primaryActionLoading, children: "Save and Continue to Launch!" }) }) })] })) : (_jsx("div", { className: "w-full flex flex-wrap justify-center", children: _jsx("div", { className: "flex max-w-xl w-full mb-16 sm:mb-0", children: _jsx(Button, { variant: ButtonVariant.Salmon, onClick: standaloneButton, disabled: primaryActionLoading, children: "Save" }) }) })), _jsx("div", { className: "w-full my-4" }), _jsx("div", { className: "w-full flex-auto" })] })] }));
};
export default ExperienceScreen;
